// Atomic address + coordinate helpers extracted from FullscreenPoiModal
// Non-React pure utilities (no side effects) for reuse.

export const ADDRESS_LABEL_KEYWORDS = ['地址', '地址：', '地點', '地点', 'address', 'location', 'addr', '地址(address)', '地點地址'];
export const ADDRESS_VALUE_KEYS = [
  'fullAddress', 'full_address', 'formatted_address', 'formattedAddress',
  'address', 'address_text', 'addressText', 'address_line', 'addressLine', 'addressLine1', 'addressLine2',
  'line1', 'line2', 'street', 'street1', 'street2', 'streetName', 'street_address', 'streetAddress',
  'neighborhood', 'suburb', 'district', 'county', 'town', 'township', 'village',
  'city', 'cityName', 'municipality', 'prefecture', 'state', 'stateName', 'province', 'region', 'area',
  'postal', 'postalCode', 'postal_code', 'zip', 'zipCode', 'zip_code',
  'country', 'countryName', 'nation'
];
const NUMERIC_COORDINATE_REGEX = /^-?\d+(?:\.\d+)?$/;

export const normalizeAddressValue = (input: any): string | undefined => {
  if (input === null || typeof input === 'undefined') return undefined;
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed || trimmed === '-' || trimmed === '—' || trimmed === '--' || trimmed === '[object Object]') return undefined;
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try { return normalizeAddressValue(JSON.parse(trimmed)); } catch { return trimmed; }
    }
    return trimmed;
  }
  if (Array.isArray(input)) {
    const parts = input.map(i => normalizeAddressValue(i)).filter((p): p is string => Boolean(p));
    if (!parts.length) return undefined;
    const unique = Array.from(new Set(parts));
    if (unique.length && unique.every(part => NUMERIC_COORDINATE_REGEX.test(part.replace(/,/g, '')))) return undefined;
    return unique.join(' ');
  }
  if (typeof input === 'object') {
    const directParts: string[] = [];
    for (const key of ADDRESS_VALUE_KEYS) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        const val = normalizeAddressValue((input as any)[key]);
        if (val) directParts.push(val);
      }
    }
    if (!directParts.length) {
      const otherParts = Object.values(input).map(v => normalizeAddressValue(v)).filter((p): p is string => Boolean(p));
      if (!otherParts.length) return undefined;
      const uniqueOther = Array.from(new Set(otherParts));
      if (uniqueOther.length && uniqueOther.every(part => NUMERIC_COORDINATE_REGEX.test(part.replace(/,/g, '')))) return undefined;
      return uniqueOther.join(' ');
    }
    const uniqueDirect = Array.from(new Set(directParts));
    if (uniqueDirect.length && uniqueDirect.every(part => NUMERIC_COORDINATE_REGEX.test(part.replace(/,/g, '')))) return undefined;
    return uniqueDirect.join(' ');
  }
  return undefined;
};

export const extractAddressFromMeta = (meta?: Array<{ label?: string; value?: any }>): string | undefined => {
  if (!Array.isArray(meta)) return undefined;
  for (const entry of meta) {
    const rawLabel = entry && typeof entry.label === 'string' ? entry.label.trim() : '';
    if (!rawLabel) continue;
    const lower = rawLabel.toLowerCase();
    const matches = ADDRESS_LABEL_KEYWORDS.some(keyword => rawLabel.includes(keyword) || lower.includes(keyword));
    if (!matches) continue;
    const value = normalizeAddressValue(entry?.value);
    if (value) return value;
  }
  return undefined;
};

export const firstAddressFrom = (...sources: any[]): string | undefined => {
  for (const source of sources) {
    const candidate = normalizeAddressValue(source);
    if (candidate) return candidate;
  }
  return undefined;
};
